<template>
  <div class="blissrose-evaluation">
    <!-- 列表 / 报告两态：报告铺整页，清单在它打开时让位 -->
    <template v-if="view === 'list'">
      <el-card shadow="never" class="table-card">
        <div slot="header" class="table-header">
          <span>模型评估</span>
          <div class="table-header-right">
            <el-input
              v-model="keyword"
              class="keyword-input"
              size="small"
              placeholder="按评估名 / 模型 / 数据集筛选"
              prefix-icon="el-icon-search"
              clearable
            />
            <el-select v-model="stateFilter" class="state-select" size="small" clearable placeholder="全部结论">
              <el-option
                v-for="(meta, key) in CONCLUSION_META"
                :key="key"
                :label="meta.label"
                :value="key"
              />
            </el-select>
            <span class="total-count">共 {{ filteredEvaluations.length }} 条</span>
            <el-button size="small" icon="el-icon-refresh" @click="loadEvaluations">刷新</el-button>
            <el-button
              type="danger"
              size="small"
              icon="el-icon-delete"
              :disabled="!selectedCount"
              :loading="batchDeleting"
              @click="handleBatchDelete"
            >{{ batchLabel }}</el-button>
            <el-button
              type="primary"
              size="small"
              icon="el-icon-video-play"
              @click="dispatchVisible = true"
            >发起评估</el-button>
          </div>
        </div>

        <!-- 单源不可达：照列另一侧，顶部说明缺了什么 -->
        <el-alert
          v-if="!sources.runs"
          class="source-alert"
          type="warning"
          :closable="false"
          show-icon
          title="进行中任务暂不可用：评估执行器连接失败，列表只含已归档部分"
        />
        <el-alert
          v-if="!sources.archived"
          class="source-alert"
          type="warning"
          :closable="false"
          show-icon
          title="归档信息暂不可用：平台服务连接失败，归档结论暂缺"
        />

        <el-table
          v-loading="loading"
          :data="filteredEvaluations"
          stripe
          border
          size="small"
          style="width:100%"
          empty-text="暂无数据"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="40" :selectable="canDelete" />
          <el-table-column label="评估 ID" min-width="150" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <span class="mono">{{ row.run_name || row.eval_id }}</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="96">
            <template slot-scope="{ row }">
              <!-- 单行：产物更新时间进了悬停提示；停滞时挂黄叹号 -->
              <el-tooltip :content="stateHint(row)" :disabled="!stateHint(row)" placement="top">
                <span>
                  <el-tag :type="stateMeta(row).tag" size="mini">{{ stateMeta(row).label }}</el-tag>
                  <i v-if="isStale(row)" class="el-icon-warning stale" />
                </span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="模型" min-width="140" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ modelLabel(row) }}</template>
          </el-table-column>
          <el-table-column label="数据集 / 划分" min-width="170" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <span class="mono">{{ datasetLabel(row) }}</span>
              <span class="cell-sub"> · {{ dash(row.split) }} / {{ dash(row.profile) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="结论" width="80">
            <template slot-scope="{ row }">
              <el-tag :type="conclusionMeta(row.conclusion_state).tag" size="mini">
                {{ conclusionMeta(row.conclusion_state).label }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="总覆盖率" width="80">
            <template slot-scope="{ row }">{{ rateText(row.coverage_rate) }}</template>
          </el-table-column>
          <!-- 产物齐全与归档是一条链：合列取链路落点（缺件 → 待归档 → 已归档/失败），细节进悬停 -->
          <el-table-column label="产物 / 归档" width="110">
            <template slot-scope="{ row }">
              <el-tooltip :content="chainTooltip(row)" :disabled="!chainTooltip(row)" placement="top">
                <span>
                  <el-tag v-if="chainMeta(row)" :type="chainMeta(row).tag" size="mini">
                    {{ chainMeta(row).label }}
                  </el-tag>
                  <span v-else-if="reportDeleted(row)" class="muted">已删除</span>
                  <span v-else class="muted">-</span>
                </span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="派发人" width="76" show-overflow-tooltip>
            <template slot-scope="{ row }">{{ dash(row.dispatched_by) }}</template>
          </el-table-column>
          <el-table-column label="派发时间" width="140">
            <template slot-scope="{ row }">{{ formatTime(row.dispatched_at) }}</template>
          </el-table-column>
          <el-table-column label="完成时间" width="140">
            <template slot-scope="{ row }">{{ formatTime(row.eval_finished_at || row.finished_at) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="150" align="center">
            <template slot-scope="{ row }">
              <el-button v-if="row.has_log" type="text" size="mini" @click="openLog(row)">详情</el-button>
              <el-button
                v-if="isRunning(row) && !isStopping(row)"
                type="text"
                size="mini"
                class="danger-text"
                :loading="stoppingName === row.run_name"
                @click="handleStop(row)"
              >停止</el-button>
              <!-- 归档判据由训练机给：终态且产物齐才给入口 -->
              <el-button
                v-if="canPush(row)"
                type="text"
                size="mini"
                :loading="pushingName === row.run_name"
                @click="handlePush(row)"
              >归档</el-button>
              <el-button v-if="row.archived" type="text" size="mini" @click="openReport(row)">查看报告</el-button>
              <!-- 在跑的不给删，与「停止」互斥；归档在途由执行器拒绝 -->
              <el-button
                v-if="canDelete(row)"
                type="text"
                size="mini"
                class="danger-text"
                :loading="deletingName === (row.run_name || row.eval_id)"
                @click="handleDelete(row)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>

    <evaluation-report v-else :eval-id="reportId" @back="backToList" />

    <evaluation-dispatch-dialog :visible.sync="dispatchVisible" @dispatched="handleDispatched" />
    <training-detail-drawer :visible.sync="logVisible" :run="logRow" @updated="handleRowUpdated" />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import EvaluationReport from '../../components/EvaluationReport.vue';
import EvaluationDispatchDialog from '../../components/EvaluationDispatchDialog.vue';
import TrainingDetailDrawer from '../../components/TrainingDetailDrawer.vue';
import { deleteEvaluation, fetchEvalModels, fetchEvalRuns } from '../../api/blissrose/evaluation';
import { deleteRun, pushRun, stopRun } from '../../api/blissrose/training';
import { ARCHIVE_STATES, EVAL_ARCHIVED, EVAL_STATES, EVAL_STOPPING } from '../../utils/labels';
import { CONCLUSION_META, conclusionMeta, rateText, dash } from '../../utils/evalMeta';

// 清单由执行器记录与归档清单现算；进行中的任务靠轮询带进列表
const POLL_INTERVAL_MS = 10000;
// 评估中且产物超过这么久没更新，把「产物更新」标黄（与训练任务页同一阈值）
const STALE_MINUTES = 30;

/** 模型评估：清单（进行中与已归档都列）+ 报告；发起、观察、停止、归档入口都在此 */
export default {
  name: 'BlissroseEvaluation',

  components: { EvaluationReport, EvaluationDispatchDialog, TrainingDetailDrawer },

  data() {
    return {
      CONCLUSION_META,
      loading: false,
      evaluations: [],
      // 两源本次拉到没有：缺哪侧顶部挂对应说明
      sources: { runs: true, archived: true },
      modelMap: {},
      keyword: '',
      stateFilter: '',
      dispatchVisible: false,
      // 当前视图：list 清单 / report 单份报告
      view: 'list',
      reportId: '',
      logVisible: false,
      logRow: null,
      stoppingName: '',
      pushingName: '',
      deletingName: '',
      batchDeleting: false,
      selected: [],
      pollTimer: null,
    };
  },

  computed: {
    selectedCount() {
      return this.selected.length;
    },

    batchLabel() {
      return this.selectedCount ? `删除选中（${this.selectedCount}）` : '删除选中';
    },

    filteredEvaluations() {
      const kw = this.keyword.trim().toLowerCase();
      return this.evaluations.filter(row => {
        if (this.stateFilter && row.conclusion_state !== this.stateFilter) return false;
        if (!kw) return true;
        const fields = [
          row.run_name,
          row.eval_id,
          row.model_id,
          row.model_name,
          this.modelMap[row.model_id],
          row.dataset_name,
          row.dataset_ref,
          row.profile,
          row.dispatched_by,
        ];
        return fields.filter(Boolean).some(v => String(v).toLowerCase().includes(kw));
      });
    },
  },

  async mounted() {
    await this.loadEvaluations();
    this.loadModelMap();
    // 轮询只在清单页无勾选时静默刷新：换数据会打断报告阅读与勾选
    this.pollTimer = setInterval(() => {
      if (this.view === 'list' && !this.loading && !this.selected.length) this.loadEvaluations(true);
    }, POLL_INTERVAL_MS);
  },

  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  },

  methods: {
    conclusionMeta,
    rateText,
    dash,

    /** 拉取评估清单（执行器记录 ∪ 已归档报告）；silent 为定时轮询，不显示加载态 */
    async loadEvaluations(silent = false) {
      if (!silent) this.loading = true;
      try {
        const { items, sources } = (await fetchEvalRuns()).data;
        this.evaluations = items || [];
        this.sources = sources || { runs: true, archived: true };
      } catch (err) {
        // 错误提示由响应拦截器统一弹出，保留上一次的清单
      } finally {
        this.loading = false;
      }
    },

    /** 模型名映射：打开页面拉一次；失败保持空映射，模型列按行内字段回退 */
    async loadModelMap() {
      try {
        const { items } = (await fetchEvalModels()).data;
        const map = {};
        for (const rec of items || []) map[rec.model_id] = rec.name;
        this.modelMap = map;
      } catch (err) {
        // 取不到就只显示行内名称或 ID
      }
    },

    // ====== 视图切换 ======

    openReport(row) {
      this.reportId = row.eval_id;
      this.view = 'report';
    },

    backToList() {
      this.view = 'list';
      this.reportId = '';
      // 回列表时刷一次清单
      this.loadEvaluations(true);
    },

    /** 日志走训练详情抽屉：评估已隐藏曲线与进度区，抽屉里只留参数、产物与日志 */
    openLog(row) {
      this.logRow = row;
      this.logVisible = true;
    },

    /** 抽屉拉到的详情回填列表，关掉后表格仍是新的；详情里没有的字段（归档标记等）保留原值 */
    handleRowUpdated(item) {
      const index = this.evaluations.findIndex(row => row.run_name === item.run_name);
      if (index >= 0) this.$set(this.evaluations, index, { ...this.evaluations[index], ...item });
      if (this.logRow && this.logRow.run_name === item.run_name) {
        this.logRow = { ...this.logRow, ...item };
      }
    },

    async handleDispatched() {
      this.$message.info('评估已发起，进行中的任务会显示在列表中');
      await this.loadEvaluations(true);
    },

    // ====== 操作 ======

    async handleStop(row) {
      // stop_mode=force（多卡）立刻结束；单卡跑完当前一步再退
      const forceful = row.stop_mode === 'force';
      const detail = forceful
        ? '会立刻结束这次评估：当前这一步不做收尾，结果停在已产出的部分。确定停止？'
        : '会让评估收尾后退出，之后不再继续，结果按停止那一刻的状态保留。确定停止？';
      try {
        await this.$confirm(`停止「${row.run_name}」${detail}`, '停止评估', { type: 'warning' });
      } catch (err) {
        return;
      }
      this.stoppingName = row.run_name;
      try {
        // 发完信号即返回「已登记停止」的行，先填回表格
        const { item } = (await stopRun(row.run_name)).data;
        this.$message.success(forceful ? '已请求停止：立刻退出' : '已请求停止：收尾后退出');
        if (item) this.handleRowUpdated(item);
        await this.loadEvaluations(true);
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.stoppingName = '';
      }
    },

    async handlePush(row) {
      try {
        await this.$confirm(
          `归档「${row.run_name}」会把评估产物上传到 MinIO，已存在的对象会跳过，只补缺的，`
            + '传输期间请勿关闭页面。确定归档？',
          '归档产物',
          { type: 'warning' },
        );
      } catch (err) {
        return;
      }
      this.pushingName = row.run_name;
      try {
        // 不覆盖，重复归档只补缺的
        const { message } = (await pushRun(row.run_name, false)).data;
        this.$message.success(message || '已归档');
        await this.loadEvaluations(true);
      } catch (err) {
        // 失败原因由响应拦截器弹出
      } finally {
        this.pushingName = '';
      }
    },

    // ====== 展示辅助 ======

    isRunning(row) {
      return !row.archived && row.state === 'running';
    },

    /** 停止请求已登记但容器还在收尾：显示「停止中」 */
    isStopping(row) {
      return row.state === 'running' && Boolean(row.stopped_by);
    },

    /** 状态列只讲生命周期；归档行没映射到状态时退回「已归档」（归档与否归合列） */
    stateMeta(row) {
      if (this.isStopping(row)) return EVAL_STOPPING;
      if (EVAL_STATES[row.state]) return EVAL_STATES[row.state];
      return row.archived ? EVAL_ARCHIVED : EVAL_STATES.unknown;
    },

    /** 产物/归档合列的标记：按链路推进取当前落点（归档事件 → 缺件 → 待归档 → 齐全） */
    chainMeta(row) {
      if (row.archived) return EVAL_ARCHIVED;
      if (this.isRunning(row)) return null;
      if (this.reportDeleted(row)) return null;
      if (row.push_state === 'pushed') return ARCHIVE_STATES.pushed;
      if (row.push_state === 'failed') return ARCHIVE_STATES.failed;
      if (row.artifacts && !row.artifacts.complete) {
        return { tag: 'warning', label: `缺 ${(row.artifacts.missing || []).length} 件` };
      }
      if (row.push_state === 'pending') return ARCHIVE_STATES.pending;
      if (row.artifacts && row.artifacts.complete) return { tag: 'success', label: '报告齐全' };
      return null;
    },

    /** 合列的悬停提示：删除说明 → 进行中说明 → 归档原因/时间 → 缺件清单 */
    chainTooltip(row) {
      if (this.reportDeleted(row)) return '归档报告已从 MinIO 删除';
      if (this.isRunning(row)) return '评估结束后自动归档';
      const push = this.pushTooltip(row);
      if (push) return push;
      const artifacts = row.artifacts;
      if (artifacts && !artifacts.complete) {
        const missing = artifacts.missing || [];
        return missing.length ? `缺件：${missing.join('、')}` : '';
      }
      if (artifacts && artifacts.complete && row.push_state === 'pending') return '报告齐全，可归档';
      return '';
    },

    /** 执行器记着归档成功、归档清单里却没有：报告在 MinIO 上已被删除 */
    reportDeleted(row) {
      return Boolean(this.sources.archived && row.push_state === 'pushed' && !row.archived);
    },

    /** 归档入口：终态且产物齐由训练机判定 */
    canPush(row) {
      return !row.archived && row.archivable;
    },

    /** 在跑的不给勾选也不给删：与「停止」互斥；已结束的（含已停止、已归档）都能删 */
    canDelete(row) {
      return !this.isRunning(row);
    },

    /** 归档格的悬停提示：原因 + 时间 */
    pushTooltip(row) {
      const lines = [];
      if (row.push_message) lines.push(row.push_message);
      if (row.push_at) lines.push(`归档时间 ${this.formatTime(row.push_at)}`);
      return lines.join('；');
    },

    /** 模型列三段回退：映射名 → 行内名称 → ID */
    modelLabel(row) {
      return this.modelMap[row.model_id] || row.model_name || dash(row.model_id);
    },

    datasetLabel(row) {
      if (!row.dataset_name) return dash(row.dataset_ref);
      return row.dataset_version === undefined || row.dataset_version === ''
        ? String(row.dataset_name)
        : `${row.dataset_name}@${row.dataset_version}`;
    },

    isStale(row) {
      if (row.state !== 'running' || !row.last_modified) return false;
      return dayjs().diff(dayjs(row.last_modified), 'minute') >= STALE_MINUTES;
    },

    /** 状态格的悬停提示：产物最近一次更新距今多久；停滞的补一句 */
    stateHint(row) {
      if (!row.last_modified) return '';
      const text = `产物更新 ${this.relativeTime(row.last_modified)}`;
      return this.isStale(row) ? `${text}（已停滞）` : text;
    },

    /** 「产物更新」显示相隔多久 */
    relativeTime(value) {
      if (!value) return '-';
      const mtime = dayjs(value);
      const minutes = dayjs().diff(mtime, 'minute');
      if (minutes < 1) return '刚刚';
      if (minutes < 60) return `${minutes} 分钟前`;
      const hours = dayjs().diff(mtime, 'hour');
      if (hours < 24) return `${hours} 小时前`;
      return `${dayjs().diff(mtime, 'day')} 天前`;
    },

    formatTime(value) {
      return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '—';
    },

    // ====== 删除 ======

    handleSelectionChange(rows) {
      this.selected = rows || [];
    },

    /** 这条评估有哪些东西要删：执行器记录 + 本地文件；已归档的还有 MinIO 报告 */
    deleteScope(row) {
      const parts = [];
      if (!row.archived_only) parts.push('执行器上的记录、产物目录与日志');
      if (row.archived) parts.push('MinIO 上的归档报告（全部文件）');
      return parts.join('，以及 ');
    },

    /** 删除一条评估：先删执行器记录（归档在途会被拒，避免报告删掉后又被推回），再删 MinIO 归档 */
    async deleteOne(row) {
      if (!row.archived_only) await deleteRun(row.run_name);
      if (row.archived) await deleteEvaluation(row.eval_id);
    },

    /** 单删：从列表里抹掉这条评估，不可恢复 */
    async handleDelete(row) {
      try {
        await this.$confirm(
          `删除「${row.run_name || row.eval_id}」会把它从评估列表里抹掉，并删掉`
            + `${this.deleteScope(row)}，不可恢复。确定删除？`,
          '删除评估',
          { type: 'warning', confirmButtonText: '删除', confirmButtonClass: 'el-button--danger' },
        );
      } catch (err) {
        return;
      }
      this.deletingName = row.run_name || row.eval_id;
      try {
        await this.deleteOne(row);
        this.$message.success('已删除');
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      } finally {
        this.deletingName = '';
        await this.loadEvaluations(true);
      }
    },

    /** 批量删除：逐个调接口，失败的点名带出 */
    async handleBatchDelete() {
      const rows = [...this.selected];
      if (!rows.length) return;
      try {
        await this.$confirm(
          `将删除选中的 ${rows.length} 条评估：执行器上的记录、产物目录与日志，已归档的报告`
            + '连同 MinIO 上的全部文件一并删除，不可恢复。确定删除？',
          '删除评估',
          { type: 'warning', confirmButtonText: '删除', confirmButtonClass: 'el-button--danger' },
        );
      } catch (err) {
        return;
      }
      this.batchDeleting = true;
      const failed = [];
      for (const row of rows) {
        try {
          await this.deleteOne(row);
        } catch (err) {
          failed.push(row.run_name || row.eval_id);
        }
      }
      this.batchDeleting = false;
      const done = rows.length - failed.length;
      if (failed.length) {
        this.$message.warning(`已删除 ${done} 条；${failed.length} 条没删掉：${failed.join('、')}`);
      } else {
        this.$message.success(`已删除 ${done} 条`);
      }
      await this.loadEvaluations(true);
    },
  },
};
</script>

<style scoped>
.table-card {
  margin-bottom: 16px;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.keyword-input {
  width: 240px;
}

.state-select {
  width: 130px;
}

.total-count {
  color: #909399;
  font-size: 13px;
}

.source-alert {
  margin-bottom: 10px;
}

.mono {
  font-family: Consolas, Monaco, monospace;
  color: #606266;
}

.cell-sub {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

/* 评估中且产物久未更新：状态标签旁挂黄叹号 */
.stale {
  color: #e6a23c;
  margin-left: 4px;
}

.muted {
  color: #c0c4cc;
}

.danger-text {
  color: #f56c6c;
}
</style>
